import { getDb } from "./database";
import type { MediaBlobRecord, MediaKind, MediaStorageFormat } from "./schema";
import { createId, nowIso, isQuotaError } from "../app/validation";
import { getSafeErrorCode } from "../app/safeError";

const PROBE_REPORT_ID = "__moveinreport_media_probe__";
const SMALL_PROBE_BYTES = 16 * 1024;
const REALISTIC_PROBE_BYTES = 1250 * 1024;

let mediaStorageCapabilitiesPromise: Promise<MediaStorageCapabilities> | undefined;

export interface PutMediaInput {
  reportId: string;
  issueId?: string;
  roomId?: string;
  kind: MediaKind;
  blob: Blob;
  key?: string;
}

export interface PreparedMediaPayload {
  storageFormat: MediaStorageFormat;
  blob?: Blob;
  bytes?: ArrayBuffer;
  mimeType: string;
  sizeBytes: number;
}

export type HydratedMediaBlobRecord = MediaBlobRecord & {
  blob: Blob;
  storageFormat: MediaStorageFormat;
};

export interface MediaProbeResult {
  ok: boolean;
  safeErrorCode?: string;
}

export interface MediaStorageCapabilities {
  checkedAt: string;
  preferredFormat: MediaStorageFormat;
  smallBlob: MediaProbeResult;
  realisticBlob: MediaProbeResult;
  arrayBuffer: MediaProbeResult;
  storageEstimate?: {
    usage?: number;
    quota?: number;
  };
}

export async function putMediaBlob(input: PutMediaInput): Promise<HydratedMediaBlobRecord> {
  const key = input.key ?? createId(`media_${input.kind}`);
  const preferredFormat = await getPreferredMediaStorageFormat();
  try {
    const record = buildMediaRecord({
      key,
      reportId: input.reportId,
      issueId: input.issueId,
      roomId: input.roomId,
      kind: input.kind,
      payload: await prepareMediaPayload(input.blob, input.blob.type || "application/octet-stream", preferredFormat)
    });
    const db = await getDb();
    await db.put("mediaBlobs", record);
    return hydrateMediaRecord(record);
  } catch (error) {
    if (!shouldRetryMediaStorageAsArrayBuffer(error, preferredFormat)) {
      throw error;
    }
    const record = buildMediaRecord({
      key,
      reportId: input.reportId,
      issueId: input.issueId,
      roomId: input.roomId,
      kind: input.kind,
      payload: await prepareMediaPayload(input.blob, input.blob.type || "application/octet-stream", "array_buffer")
    });
    const db = await getDb();
    await db.put("mediaBlobs", record);
    return hydrateMediaRecord(record);
  }
}

export async function getMediaBlob(key: string | undefined): Promise<HydratedMediaBlobRecord | undefined> {
  if (!key) {
    return undefined;
  }
  const db = await getDb();
  const record = await db.get("mediaBlobs", key);
  return record ? hydrateMediaRecord(record) : undefined;
}

export async function deleteMediaBlob(key: string | undefined): Promise<void> {
  if (!key) {
    return;
  }
  const db = await getDb();
  await db.delete("mediaBlobs", key);
}

export async function listMediaByIssue(issueId: string): Promise<MediaBlobRecord[]> {
  const db = await getDb();
  return db.getAllFromIndex("mediaBlobs", "byIssueId", issueId);
}

export async function listMediaByReport(reportId: string): Promise<MediaBlobRecord[]> {
  const db = await getDb();
  return db.getAllFromIndex("mediaBlobs", "byReportId", reportId);
}

export async function getPreferredMediaStorageFormat(): Promise<MediaStorageFormat> {
  if (isLikelyWebKitBrowser()) {
    void getMediaStorageCapabilities().catch(() => undefined);
    return "array_buffer";
  }
  const capabilities = await getMediaStorageCapabilities();
  return capabilities.smallBlob.ok && capabilities.realisticBlob.ok ? "blob" : "array_buffer";
}

export function prepareMediaPayload(
  blob: Blob,
  mimeType = blob.type || "application/octet-stream",
  storageFormat: MediaStorageFormat
): Promise<PreparedMediaPayload> {
  return storageFormat === "blob"
    ? Promise.resolve({
        storageFormat,
        blob,
        mimeType,
        sizeBytes: blob.size
      })
    : blob.arrayBuffer().then((bytes) => ({
        storageFormat,
        bytes,
        mimeType,
        sizeBytes: blob.size
      }));
}

export function buildMediaRecord(input: {
  key: string;
  reportId: string;
  issueId?: string;
  roomId?: string;
  kind: MediaKind;
  payload: PreparedMediaPayload;
  createdAt?: string;
}): MediaBlobRecord {
  return {
    key: input.key,
    reportId: input.reportId,
    issueId: input.issueId,
    roomId: input.roomId,
    kind: input.kind,
    storageFormat: input.payload.storageFormat,
    blob: input.payload.blob,
    bytes: input.payload.bytes,
    mimeType: input.payload.mimeType,
    sizeBytes: input.payload.sizeBytes,
    createdAt: input.createdAt ?? nowIso()
  };
}

export function hydrateMediaRecord(record: MediaBlobRecord): HydratedMediaBlobRecord {
  if (record.blob instanceof Blob) {
    return {
      ...record,
      storageFormat: record.storageFormat ?? "blob",
      blob: record.blob
    };
  }
  if (record.bytes instanceof ArrayBuffer) {
    return {
      ...record,
      storageFormat: "array_buffer",
      blob: new Blob([record.bytes], { type: record.mimeType || "application/octet-stream" })
    };
  }
  throw new Error("Stored media is unavailable in this browser.");
}

export function shouldRetryMediaStorageAsArrayBuffer(
  error: unknown,
  attemptedFormat: MediaStorageFormat
): boolean {
  return attemptedFormat === "blob" && !isQuotaError(error);
}

export function getMediaStorageCapabilities(): Promise<MediaStorageCapabilities> {
  mediaStorageCapabilitiesPromise ??= probeMediaStorageCapabilities().catch((error) => {
    mediaStorageCapabilitiesPromise = undefined;
    throw error;
  });
  return mediaStorageCapabilitiesPromise;
}

async function probeMediaStorageCapabilities(): Promise<MediaStorageCapabilities> {
  const checkedAt = nowIso();
  const db = await getDb();
  const storageEstimate = await getStorageEstimate();
  const smallBlob = await runBlobProbe(db, "small", SMALL_PROBE_BYTES);
  const realisticBlob = await runBlobProbe(db, "realistic", REALISTIC_PROBE_BYTES);
  const arrayBuffer = await runArrayBufferProbe(db, SMALL_PROBE_BYTES);
  const preferredFormat =
    !isLikelyWebKitBrowser() && smallBlob.ok && realisticBlob.ok ? "blob" : "array_buffer";

  return {
    checkedAt,
    preferredFormat,
    smallBlob,
    realisticBlob,
    arrayBuffer,
    storageEstimate
  };
}

async function runBlobProbe(
  db: Awaited<ReturnType<typeof getDb>>,
  label: "small" | "realistic",
  bytes: number
): Promise<MediaProbeResult> {
  const key = `${PROBE_REPORT_ID}_${label}_${createId("probe")}`;
  try {
    const record: MediaBlobRecord = {
      key,
      reportId: PROBE_REPORT_ID,
      kind: "photo_full",
      storageFormat: "blob",
      blob: new Blob([new Uint8Array(bytes)], { type: "application/octet-stream" }),
      mimeType: "application/octet-stream",
      sizeBytes: bytes,
      createdAt: nowIso()
    };
    await db.put("mediaBlobs", record);
    const stored = await db.get("mediaBlobs", key);
    const hydrated = stored ? hydrateMediaRecord(stored) : undefined;
    return { ok: hydrated?.blob.size === bytes };
  } catch (error) {
    return { ok: false, safeErrorCode: getSafeErrorCode(error, `MEDIA_${label}_BLOB_PROBE_FAILED`) };
  } finally {
    await db.delete("mediaBlobs", key).catch(() => undefined);
  }
}

async function runArrayBufferProbe(
  db: Awaited<ReturnType<typeof getDb>>,
  bytes: number
): Promise<MediaProbeResult> {
  const key = `${PROBE_REPORT_ID}_array_buffer_${createId("probe")}`;
  try {
    const record: MediaBlobRecord = {
      key,
      reportId: PROBE_REPORT_ID,
      kind: "photo_full",
      storageFormat: "array_buffer",
      bytes: new ArrayBuffer(bytes),
      mimeType: "application/octet-stream",
      sizeBytes: bytes,
      createdAt: nowIso()
    };
    await db.put("mediaBlobs", record);
    const stored = await db.get("mediaBlobs", key);
    const hydrated = stored ? hydrateMediaRecord(stored) : undefined;
    return { ok: hydrated?.blob.size === bytes };
  } catch (error) {
    return { ok: false, safeErrorCode: getSafeErrorCode(error, "MEDIA_ARRAY_BUFFER_PROBE_FAILED") };
  } finally {
    await db.delete("mediaBlobs", key).catch(() => undefined);
  }
}

async function getStorageEstimate(): Promise<{ usage?: number; quota?: number } | undefined> {
  try {
    if (typeof navigator === "undefined" || !navigator.storage?.estimate) {
      return undefined;
    }
    const estimate = await navigator.storage.estimate();
    return {
      usage: estimate.usage,
      quota: estimate.quota
    };
  } catch {
    return undefined;
  }
}

function isLikelyWebKitBrowser(): boolean {
  const userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(userAgent) || (/Safari\//.test(userAgent) && !/Chrome\/|CriOS\/|Edg\//.test(userAgent));
}
